/**
 * consents.js (r7.6) — the client side of scoped marketing consent.
 *
 *   scopes    snt_marketing (the platform pool)  |  tenant_marketing + tenant_id (one tenant's own list)
 *   channels  email | sms | whatsapp | push       — a checkbox grants every channel; /me/consents fine-tunes
 *
 *   grant / withdraw → POST /api/auth/consents { grants|withdrawals: [{scope, tenant_id?, channel}], source }
 *   mine             → GET  /api/auth/consents/me  { consents: rows, summary: { snt_marketing: [ch], tenant_marketing: { slug: [ch] } } }
 *   unsubscribe      → POST /api/unsubscribe/:token (r7.6 row token)  |  GET /api/notify/unsubscribe?t= (r7.5 campaign token)
 *
 * Nothing here ever blocks a purchase: callers fire consent writes after the user's real action and swallow failures.
 */
import { api } from '@/lib/api';
import { syncTopic } from '@/lib/native';   // r7.6 step 9 (no-op on the web)

export const CHANNELS = ['email', 'sms', 'whatsapp', 'push'];
export const PLATFORM = 'snt';

const pairs = (scope, tenantId, channels = CHANNELS) => channels.map((channel) => (
  scope === 'tenant_marketing' ? { scope, tenant_id: tenantId, channel } : { scope, channel }
));

export const consents = {
  mine: () => api.get('/api/auth/consents/me'),
  /** grant every channel for one or more scopes: [{ scope, tenant_id? }] */
  grantAll: async (scopes, source = 'app') => {
    const r = await api.post('/api/auth/consents', { grants: scopes.flatMap((s) => pairs(s.scope, s.tenant_id)), source });
    for (const s of scopes) if (s.scope === 'tenant_marketing' && s.tenant_id) syncTopic({ tenantSlug: s.tenant_id, pushConsent: true });
    return r;
  },
  set: ({ grants = [], withdrawals = [], source = 'app' }) => api.post('/api/auth/consents', { grants, withdrawals, source }),
  unsubscribeToken: (token) => api.post(`/api/unsubscribe/${encodeURIComponent(token)}`),
  unsubscribeCampaign: (t) => api.get(`/api/notify/unsubscribe?t=${encodeURIComponent(t)}`),
};

/** Which scopes a checkout / signup should offer: the tenant's own (when not the platform) and the platform's. */
export function scopesFor({ tenantSlug = null, tenantName = null } = {}) {
  const out = [];
  if (tenantSlug && tenantSlug !== PLATFORM) out.push({ key: 'tenant', scope: 'tenant_marketing', tenant_id: tenantSlug, name: tenantName || tenantSlug });
  out.push({ key: 'snt', scope: 'snt_marketing', tenant_id: null, name: 'ShowzNTicketz' });
  return out;
}

/** true when the summary already carries an active grant on any channel for that scope */
export function alreadyGranted(summary, scope) {
  if (!summary) return false;
  if (scope.scope === 'snt_marketing') return (summary.snt_marketing || []).length > 0;
  return ((summary.tenant_marketing || {})[scope.tenant_id] || []).length > 0;
}

/** Pending consent captured before the session existed (signup) — flushed once the token is set. */
const PENDING_KEY = 'snt:consent:pending';
export function stashPending(scopes, source) {
  try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ scopes, source, at: Date.now() })); } catch { /* ignore */ }
}
export async function flushPending() {
  let raw = null;
  try { raw = sessionStorage.getItem(PENDING_KEY); sessionStorage.removeItem(PENDING_KEY); } catch { return null; }
  if (!raw) return null;
  try {
    const { scopes, source, at } = JSON.parse(raw);
    if (!Array.isArray(scopes) || !scopes.length || Date.now() - at > 3600e3) return null;
    return await consents.grantAll(scopes, source || 'signup');
  } catch { return null; }
}
